import { getAverageConfidence } from './confidence.state.utils';

/** Confidence score value indicating user-verified/swapped data (100%) */
export const VERIFIED_CONFIDENCE_SCORE = 100;

export const UPDATE_FIELD_CONFIDENCE = 'UPDATE_FIELD_CONFIDENCE';
export const SET_INITIAL_FIELDSETS = 'SET_INITIAL_FIELDSETS';
export const REGISTER_FIELDSET = 'REGISTER_FIELDSET';
export const RESET_STATE = 'RESET_STATE';

interface UpdateFieldConfidencePayload {
  fieldSet: string;
  fieldNames: string | string[];
  confidence: number;
}

interface RegisterFieldsetPayload {
  fieldSet: string;
  /** Already normalized to the 0-1 range, including `_total`. */
  scores: Record<string, number> & { _total: number };
}

type SetInitialFieldsetsPayload = string[];

type Actions =
  | {
      type: typeof UPDATE_FIELD_CONFIDENCE;
      payload: UpdateFieldConfidencePayload;
    }
  | { type: typeof SET_INITIAL_FIELDSETS; payload: SetInitialFieldsetsPayload }
  | { type: typeof REGISTER_FIELDSET; payload: RegisterFieldsetPayload }
  | { type: typeof RESET_STATE; payload: ConfidenceState };

export const initialState = {
  fieldsets: {
    summary: {
      _total: 0,
    },
    firmoAccountInfo: {
      _total: 0,
    },
    firmoANI: {
      _total: 0,
    },
    firmoDetails: {
      _total: 0,
    },
    firmoIndustry: {
      _total: 0,
    },
    firmoOrganizationContact: {
      _total: 0,
    },
    firmoSecurityContact: {
      _total: 0,
    },
    submissionCoverage: {
      _total: 0,
    },
    questions: {
      _total: 0,
    },
  },
  overallConfidence: 0,
};

/**
 * @example fieldsets: { summary: { field1: 0.8, field2: 0.9, _total: 0.85 } }
 */
export interface ConfidenceState {
  fieldsets: {
    [key: string]: Record<string, number> & { _total: number };
  };
  overallConfidence: number;
}

type ConfidenceFieldsetKeys = keyof ConfidenceState['fieldsets'];

export const reducer = (
  state: ConfidenceState,
  action: Actions
): ConfidenceState => {
  switch (action.type) {
    case UPDATE_FIELD_CONFIDENCE: {
      const { fieldSet, fieldNames, confidence } = action.payload;
      const group = fieldSet; // fieldSet is already a string

      // Error, we don't have a known group
      if (!state.fieldsets[group]) {
        console.error(
          `Field group "${group}" is not valid, did you register it?`
        );
        return state;
      }

      // the following assumes you're updating all fields to the same confidence (e.g. 100%) per the requirements
      const fieldnames = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
      const nextFieldUpdates = fieldnames.reduce(
        (prev, fn) => ({ ...prev, [fn]: confidence }),
        {}
      );

      // perform the update first to make aggreation straight forward
      const nextState = {
        ...state,
        fieldsets: {
          ...state.fieldsets,
          [group]: {
            ...state.fieldsets[group],
            ...nextFieldUpdates,
          },
        },
      };

      // Define which groups should use API-provided scores (all others calculate locally)
      const groupsToUseAPI = [
        'firmoAccountInfo',
        'firmoANI',
        'firmoDetails',
        'firmoIndustry',
        'firmoOrganizationContact',
        'firmoSecurityContact',
        'submissionCoverage',
      ];

      // Calculate locally for all groups except those that should use API-provided scores
      const shouldCalculateLocally = !groupsToUseAPI.includes(group);

      if (shouldCalculateLocally) {
        const { _total, ...selectedGroup } = nextState.fieldsets[group];
        const selectedGroupConfidenceList = Object.keys(selectedGroup).map(
          (fieldNameKey: string) => selectedGroup[fieldNameKey]
        );

        const nextGroupTotal = getAverageConfidence(
          selectedGroupConfidenceList
        );

        nextState.fieldsets[group]._total = nextGroupTotal;

        const groupTotalsList = Object.keys(nextState.fieldsets).map(
          (groupKey) =>
            nextState.fieldsets[groupKey as ConfidenceFieldsetKeys]._total
        );

        const nextTotalConfidence = getAverageConfidence(groupTotalsList);

        nextState.overallConfidence = nextTotalConfidence;
      }

      return nextState;
    }

    case REGISTER_FIELDSET: {
      const { fieldSet, scores } = action.payload;

      // Groups already present were registered from the submission response and
      // hold server truth (or a confidence the user just clicked to 100%), so a
      // late registration must never clobber them.
      if (state.fieldsets[fieldSet]) {
        return state;
      }

      // `overallConfidence` is owned by the API for extractor-sourced
      // submissions; registering a group must not move it.
      return {
        ...state,
        fieldsets: {
          ...state.fieldsets,
          [fieldSet]: scores,
        },
      };
    }

    case SET_INITIAL_FIELDSETS: {
      const initialFieldsets: string[] = action.payload;

      const nextState = initialFieldsets.reduce(
        (acc, fieldSet) => ({
          ...acc,
          [fieldSet]: { _total: 0 },
        }),
        {}
      );

      return {
        ...state,
        fieldsets: {
          ...state.fieldsets,
          ...nextState,
        },
        overallConfidence: 0,
      };
    }

    case RESET_STATE: {
      // Reset the entire state with new confidence scores from API.
      // This updates group totals for firmo fieldsets (accountInfo, details, etc.)
      // while preserving locally calculated totals for questions and other dynamic groups:
      // groups the API sweep doesn't cover — a question form a panel registered
      // for itself, as the renewal app does — are kept, since dropping them here
      // would unregister the group for good and pin its confidence at 0%.
      return {
        ...action.payload,
        fieldsets: {
          ...state.fieldsets,
          ...action.payload.fieldsets,
        },
      };
    }

    default:
      return state;
  }
};
